import context from '@truckdown/components-systems';
import { AddressModel, AddCreditCardModel, Currency } from '@truckdown/billing';
import { defineComponent, ref, Ref, onMounted } from "vue";
import { showMessage } from '../../Utilities/utilities';
import { CreditCardValidator } from './Validation/CreditCardValidator';
import { AddressValidator } from './Validation/AddressValidator';
import validationError from '../../Validation/Error/Error.vue';
import { ICreditCardService } from '@truckdown/billing';
import { ILookupService } from '@truckdown/systems';

interface IData {
    isLoading: Ref<boolean>,
    showError: Ref<boolean>,
    accountId: Ref<string>,
    cardToAdd: Ref<AddCreditCardModel>,
    address: Ref<AddressModel>,
    responseErrors: Ref<any>,
    creditCardValidator: Ref<any>,
    addressValidator: Ref<any>
    postalPlaceholder: Ref<string>,
    errorMessage: Ref<string>,
    selectableYears: Ref<number[]>,
    selectableMonths: Ref<string[]>,
    selectableRegions: Ref<string[]>
};

const getInitialData = function (props: any) {
    let addressModel = {
        line1: "",
        line2: "",
        city: "",
        regionCode: "",
        countryCode: "",
        postalCode: "",
        currency: Currency.CAD
    } as AddressModel;

    let cardToAdd = {
        accountId: props.accountid,
        name: "",
        address: addressModel,
        number: "",
        expiryMonth: 0,
        expiryYear: 0,
        securityCode: ""
    } as AddCreditCardModel;

    return {
        isLoading: ref<boolean>(false),
        showError: ref<boolean>(false),
        errorMessage: ref<string>(""),
        accountId: ref<String>(props.accounid),
        cardToAdd: ref<AddCreditCardModel>(cardToAdd as AddCreditCardModel),
        address: ref<AddressModel>(addressModel),
        responseErrors: ref<any>({}),
        postalPlaceholder: ref<string>('ex. A1A 1A1'),
        creditCardValidator: ref<any>(new CreditCardValidator(cardToAdd)),
        addressValidator: ref<any>(new AddressValidator(cardToAdd.address)),
        selectableYears: ref<number[]>(Array.from({ length: 17 }, (v, i) => new Date().getFullYear() + i)),
        selectableMonths: ref<string[]>(Array.from({ length: 12 }, (_, i) => String(i + 1))),
        selectableRegions: ref<string[]>([]),
    } as IData;
}

const getMethods = function (data: IData, ctx: any) {
    return {
        clearResponseErrors: function (type: string) {
            switch (type) {
                case "address":
                    data.responseErrors.value['line1'] = "";
                    data.responseErrors.value['postal'] = "";
                    break;
                case "code":
                    data.responseErrors.value['code'] = "";
                    break;
                case "number":
                    data.responseErrors.value['number'] = "";
                    break;
            }
        },
        changeCountry: function () {
            getRegions(data.address.value.countryCode, data);
            data.address.value.currency = getCurrency(data.address.value.countryCode);
            data.address.value.regionCode = "";
            if (data.address.value.countryCode == "US") {
                data.postalPlaceholder.value = "ex. 12345";
            } else {
                data.postalPlaceholder.value = "ex. A1A 1A1";
            }
        },
        saveCard: async function () {
            if (!data.isLoading.value) {
                data.isLoading.value = true;
                try {
                    if (data.creditCardValidator.value.validate() && data.addressValidator.value.validate()) {
                        data.errorMessage.value = "";
                        data.responseErrors.value = {};
                        let creditCardActionsService = await context.getService<ICreditCardService>('ICreditCardService');
                        data.cardToAdd.value.address = data.address.value;
                        if (data.cardToAdd.value != null) {
                            var response = await creditCardActionsService.add(data.cardToAdd.value);
                            if (!response.ok) {
                                if (response.message) {
                                    processResponseMessage(response.message, data);
                                } else {
                                    data.errorMessage.value = "Make sure credit card details are valid";
                                }
                            } else {
                                showMessage("Credit Card was successfully added.", "success", true, true);
                                ctx.emit(events.save, response.card);
                            }
                        }
                    }
                }
                catch (err) {
                    data.errorMessage.value = "Make sure credit card details are valid";
                    console.error(err);
                }
                finally {
                    data.isLoading.value = false;
                }
            }
        },
        cancel: function () {
            ctx.emit(events.cancel);
        }
    }
}

function getCurrency(countryCode: string | undefined): Currency {
    const countryMappings: Record<string, Currency> = {
        "CA": Currency.CAD,
        "US": Currency.USD
    };

    if (countryCode && (countryCode in countryMappings)) {
        return countryMappings[countryCode];
    }
    return Currency.CAD;
};

const getRegions = async function (country: string | undefined, data: IData) {
    const lookupService = await context.getService<ILookupService>('ILookupService');
    let regionLookupResult = await lookupService.getRegions(country);
    if (country) {
        regionLookupResult = regionLookupResult.filter(item => item.country == country);
    }
    data.selectableRegions.value = Array.from(new Set(regionLookupResult.map(item => item.code)));
};

const processResponseMessage = function (msg: string, data: IData) {
    data.errorMessage.value = msg;

    if (msg.includes("Unsupported credit card type.")) {
        data.responseErrors.value['number'] = msg;
        data.errorMessage.value = "";
    }

    if (msg.includes("Invalid security code.")) {
        data.responseErrors.value['code'] = msg;
        data.errorMessage.value = "";
    }

    if (msg.includes("Address and Zip/Postal code on card does not match records.")) {
        data.responseErrors.value['line1'] = msg;
        data.responseErrors.value['postal'] = msg;
        data.errorMessage.value = "";
    }
}

const events = {
    cancel: 'cancel',
    save: 'save'
};

export default defineComponent({
    props: {
        accountid: String
    },
    components: {
        'error': validationError
    },
    emits: [events.save, events.cancel],
    setup: function (props, ctx) {

        let data = getInitialData(props);
        let methods = getMethods(data, ctx);

        onMounted(async () => {
            data.creditCardValidator.value = new CreditCardValidator(data.cardToAdd.value);
            data.addressValidator.value = new AddressValidator(data.address.value);
            await getRegions(data.cardToAdd.value.address.countryCode, data);
        });

        return {
            ...data,
            ...methods
        };
    }
});